import { Link, Stack } from '@mui/material';
import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { CodeSnippetCard } from '../../../common/components/CodeSnippetCard';
import { ResourceSummary } from '../../../common/components/ResourceSummary';
import { ResourceInfoItem } from '../../../common/components/ResourceSummaryInfo';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { PaginationResult } from '../../../common/types/pagination';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { PATHS } from '../../../routing/routes';
import { MatchedCertificatesTable } from '../components/MatchedCertificatesTable';
import { OrganizationCertificateDetail } from '../models/OrganizationCertificateDetail';
import { ReportCertificate } from '../models/ReportCertificate';
import { OrganizationCertificatesService } from '../services/OrganizationCertificatesService';

type State = BaseState & {
  loading: boolean;
};

type Props = WithRouterProps & {};

class Controller extends BaseController<Props, State> {
  private readonly certificateService = new OrganizationCertificatesService();
  private readonly certificateId: number;

  private certificate: OrganizationCertificateDetail | null = null;
  private initialMatchedCertificates: PaginationResult<ReportCertificate> | null = null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ certificateId: this.certificateId } = this.parseNumericIds(['certificateId']));

    this.state = { ...super.getBaseState(), loading: true };
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.certificateService.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canAccessOrganizationCertificate();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    this.fetchData();
  }

  render() {
    const { loading } = this.state;

    const hasPrivateKey = this.certificate?.getPublicKey().getHasPrivateKey() ?? null;
    const isSelfSigned = this.certificate?.getIsSelfSigned() ?? null;
    const isCa = this.certificate?.getIsCa() ?? null;
    const publicKeyId = this.certificate?.getPublicKey().getId() ?? null;
    const value = this.certificate?.getValue().toLocalizedObject() ?? null;
    const encoded = this.certificate?.getEncoded() ?? null;

    const detailsBoxItems: ResourceInfoItem[] = [
      { name: t('hasPrivateKey'), data: hasPrivateKey ? t('yes') : t('no') },
      { name: t('selfSigned'), data: isSelfSigned ? t('yes') : t('no') },
      { name: t('caCertificate'), data: isCa ? t('yes') : t('no') },
    ];
    if (publicKeyId) {
      detailsBoxItems.push({
        name: t('publicKey'),
        data: (
          <Link
            href={UrlHelper.generatePath(PATHS.KEY, { keyId: publicKeyId.toString() })}
            aria-label={t('a11y.organization.certificate.publicKeyLabel', { keyId: publicKeyId }) as string}
          >
            {publicKeyId}
          </Link>
        ),
      });
    }

    return this.renderBase(
      <SecondaryNav title={t('certificateWithId', { certificateId: this.certificateId })} getBreadcrumbs={this.getBreadcrumbs}>
        <Stack gap={2}>
          <BackButton />
          <ResourceSummary
            title={t('detailsForCertificateId', { certificateId: this.certificateId })}
            loading={loading}
            items={detailsBoxItems}
            valueTitle=""
            value={value}
            metadataValue={null}
            metadataTitle=""
          />
          {(!this.initialMatchedCertificates || this.initialMatchedCertificates.data.length > 0) && (
            <MatchedCertificatesTable certificateId={this.certificateId} initialData={this.initialMatchedCertificates} withPrefill parentLoading={loading} />
          )}
          {!!encoded && <CodeSnippetCard title={t('pemEncodedCertificate')}>{encoded}</CodeSnippetCard>}
        </Stack>
      </SecondaryNav>
    );
  }

  private async fetchData() {
    try {
      const { certificate, initialMatchedCertificates } = await this.certificateService.getOrganizationCertificate(this.certificateId);
      this.certificate = certificate;
      this.initialMatchedCertificates = initialMatchedCertificates;
      this.setState({ loading: false });
    } catch (e: unknown) {
      this.setState({ loading: false });
      this.handleError(e, { invalidUrl: t('nonExistingCertificate') });
    }
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getCertificateBreadcrumbs({ certificateId: this.certificateId.toString() });
  }
}

export const OrganizationCertificateController = withRouter(Controller);
